import { useCallback, useEffect, useState } from 'react';
import { ErroDeArea } from '../../design/Estados';
import { reais } from '../../premiacao';
import { mostraValor, useValoresOcultos } from '../../design/sigilo';
import { regrasDaPremiacao } from '../../vendas';
import type { ParametrosPorBase, RegrasParaOBalconista } from '../../vendas';
import './pagando.css';

/**
 * COMO FUNCIONA A PREMIAÇÃO — a página de ajuda do balconista (o 1.0 tinha; aqui com os números
 * VIGENTES que vêm do servidor e exemplos calculados com eles). Mesma regra do motor de prêmio:
 *   vitamina: pelos cards (entre os dois, proporcional; abaixo do card 2, não premia);
 *   pré-vencido e paradão: o MAIOR entre o prêmio base e X% do que passar do preço base, até o
 *   teto; abaixo do preço base ou sem preço base, não premia.
 */

function premioPorBase(p: ParametrosPorBase, base: number, preco: number): number {
  if (preco < base) return 0;
  const parte = ((preco - base) * Number(p.pctDoAumento)) / 100;
  return Math.min(Math.max(Number(p.premioBase), parte), Number(p.teto));
}

export function ComoFunciona() {
  const ocultos = useValoresOcultos();
  const [r, setR] = useState<RegrasParaOBalconista | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    void regrasDaPremiacao().then((x) => (x.ok ? setR(x.dados) : setErro(true)));
  }, []);
  useEffect(carregar, [carregar]);

  return (
    <div className="pg cfu">
      <div className="pg-topo">
        <h1>Como funciona a premiação</h1>
        <p>Tudo o que conta para você ganhar — e o que faz perder.</p>
      </div>
      {erro ? (
        <ErroDeArea oQue="as regras" aoTentar={carregar} />
      ) : !r ? (
        <div className="pg-carregando" aria-busy="true" />
      ) : (
        <>
          <section className="pg-item cfu-bloco">
            <h2>1. A semana e a meta</h2>
            <p>
              A semana da premiação vai de <b>sábado a sexta</b>. Para receber, você precisa ter
              pelo menos <b>{r.metaSemanal} itens confirmados</b> na semana (a meta obrigatória) —
              conta o item que o HOS confirmou como vendido por você, não só o lançado.{' '}
              <b>Abaixo da meta, a premiação da semana não é paga.</b>
            </p>
          </section>

          {r.programas.vitamina && (
            <section className="pg-item cfu-bloco">
              <h2>2. Vitamina — pelos cards</h2>
              <p>
                Cada vitamina tem até 2 cards: o <b>card 1</b> (preço maior, prêmio maior) e o{' '}
                <b>card 2</b> (preço menor). Vendeu no card 1 ou acima, ganha o prêmio do card 1.
                Entre os dois, o prêmio é proporcional. Abaixo do card 2, não premia.
              </p>
              {!ocultos && (
                <table data-sem-ordenar className="cfu-tabela">
                  <caption>
                    Exemplo: card 1 a R$ 44,90 paga R$ 10,00 · card 2 a R$ 34,90 paga R$ 5,00
                  </caption>
                  <thead>
                    <tr>
                      <th>Vendeu a</th>
                      <th>Você ganha</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>R$ 44,90 ou mais</td>
                      <td>R$ 10,00</td>
                    </tr>
                    <tr>
                      <td>R$ 39,90</td>
                      <td>R$ 7,50</td>
                    </tr>
                    <tr>
                      <td>R$ 34,90</td>
                      <td>R$ 5,00</td>
                    </tr>
                    <tr>
                      <td>abaixo de R$ 34,90</td>
                      <td>não premia</td>
                    </tr>
                  </tbody>
                </table>
              )}
              <p className="cfu-nota">
                Os cards de cada produto estão em <a href="#/pagando">O que está pagando</a>.
              </p>
            </section>
          )}

          {r.programas.preVencido && r.preVencido && (
            <BlocoPorBase numero={3} titulo="Pré-vencido" p={r.preVencido} ocultos={ocultos} />
          )}
          {r.programas.paradao && r.paradao && (
            <BlocoPorBase numero={4} titulo="Paradão" p={r.paradao} ocultos={ocultos} />
          )}

          <section className="pg-item cfu-bloco">
            <h2>O que faz PERDER a premiação</h2>
            <ul className="cfu-lista">
              <li>
                <b>Vendeu e não lançou</b> no InovaPay: o sistema confere com o HOS e mostra o que
                você deixou de ganhar.
              </li>
              <li>
                <b>Lançou em um dia e vendeu em outro</b>: lance no mesmo dia da venda.
              </li>
              <li>
                <b>Cupom no nome de outro operador</b> no HOS: a venda tem que estar no SEU cupom.
              </li>
              <li>
                <b>Devolução e cancelamento</b> descontam a premiação daquele item.
              </li>
              <li>
                <b>Lote vendido no dia em que entrou</b> (pré-vencido): não conta.
              </li>
              <li>
                <b>Lote de outro produto</b> ou venda para Mais Brasil/Formulativa (código 30): é
                proibido e corta a vitamina do mês.
              </li>
            </ul>
          </section>

          <section className="pg-item cfu-bloco">
            <h2>Perguntas rápidas</h2>
            <p>
              <b>Quando eu recebo?</b> Depois da conferência da semana com o HOS. O valor que
              aparece ao lançar é a <i>previsão</i>; o que vale é o da{' '}
              <a href="#/premiacao">Minha premiação</a>.
            </p>
            <p>
              <b>Errei um lançamento, e agora?</b> Cancele na hora pelo "Desfazer" ou em Minhas
              vendas e lance de novo certo.
            </p>
            <p>
              <b>Onde vejo o que está pagando?</b> Em <a href="#/pagando">O que está pagando</a>,
              com o preço para vender{ocultos ? '' : ' e quanto você ganha'}.
            </p>
          </section>
        </>
      )}
    </div>
  );
}

function BlocoPorBase({
  numero,
  titulo,
  p,
  ocultos,
}: {
  numero: number;
  titulo: string;
  p: ParametrosPorBase;
  ocultos: boolean;
}) {
  const base = 20;
  const precos = [20, 26, 30, 60];
  return (
    <section className="pg-item cfu-bloco">
      <h2>
        {numero}. {titulo} — pelo preço base
      </h2>
      {ocultos || !mostraValor(ocultos, p.premioBase) ? (
        <p>
          Vendeu <b>no preço base ou acima</b>: você ganha o <b>maior</b> entre o prêmio base e{' '}
          <b>{p.pctDoAumento}%</b> do que passar do preço base. Abaixo do preço base, ou produto sem
          preço base, não premia.
        </p>
      ) : (
        <p>
          Vendeu <b>no preço base ou acima</b>: você ganha o <b>maior</b> entre o prêmio base (
          {reais(Number(p.premioBase))}) e <b>{p.pctDoAumento}%</b> do que passar do preço base —
          até no máximo <b>{reais(Number(p.teto))}</b> por unidade. Abaixo do preço base, ou produto
          sem preço base, não premia.
        </p>
      )}
      {!ocultos && mostraValor(ocultos, p.premioBase) && (
        <table data-sem-ordenar className="cfu-tabela">
          <caption>Exemplo com preço base de R$ 20,00 (valores de hoje)</caption>
          <thead>
            <tr>
              <th>Vendeu a</th>
              <th>Você ganha</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>R$ 19,00</td>
              <td>não premia</td>
            </tr>
            {precos.map((x) => (
              <tr key={x}>
                <td>{reais(x)}</td>
                <td>{reais(premioPorBase(p, base, x))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
